import { useState, useEffect, useRef } from 'react';
import { blackFriday2023CheckIn, getBlackFriday2023CheckInProgress } from '@base/activity/blackFridayCheckIn/services';
import { IBlackFridayCheckInData } from 'base/interfacesV2/blackFriday';
import { useStore } from 'base/store';
import { displayCtr, trackElement, TTrackElementAction } from 'base/utils/tracker';
import { TSpElementAction } from 'base/utils/tracker-sp';

export type useBlackFridayCheckInProps = {
    currentVideoUrl: string[];
    currentStep: ISignInStep;       // 当天签到步骤
    loading: boolean;
    progressData?: IBlackFridayCheckInData;
    playCount?: number;
    onStartHandle: () => void;
    onCheckInHandle: () => void;
    onPlayHandle: () => void;
    onSeeFinalReSource: () => void;
    onReloadData: () => void;
    isLogin: boolean;
}

export type ISignInStep = 'not-started' | 'start' | 'loading' | 'pre-check' | 'checked';

export const btnTextConfig = {
    1: {
        btnText: 'GERMINATION'
    },
    2: {
        btnText: 'SOWING'
    },
    3: {
        btnText: 'WATERING'
    },
    4: {
        btnText: 'TRAINING'
    },
    5: {
        btnText: 'HARVEST'
    },
    isFinal: {
        btnText: 'REVIEW YOUR JOURNEY'
    },
    checked: {
        btnText: 'COME BACK TOMORROW'
    }
}

/**
 * start 按钮打点
 * @param {TSpElementAction} action
 */
const trackStartBtn = (action: TSpElementAction) => {
    trackElement({
        action,
        targetType: 'other',
        target: 'black_friday_check_in_start',
        relTarget: 'black_friday_check_in_start',
        module: 'black_friday_check_in'
    })
}

export default function useBlackFridayCheckIn(serverProgressData?: IBlackFridayCheckInData): useBlackFridayCheckInProps {
    // const { isLogin } = useStoreState('userStore');  // m 端一直false ...
    const { userStore } = useStore();
    const isLogin = userStore.checkAuth()

    const [currentVideoUrl, setCurrentVideoUrl] = useState<string[]>([''])  // 当前视频链接
    const [currentStep, setCurrentStep] = useState<ISignInStep>('start')
    const [progressData, setProgressData] = useState<IBlackFridayCheckInData>()
    const [loading, setLoading] = useState<boolean>(true)
    const [playCount, setPlayCount] = useState<number>(0)       // 播放视频操作


    const currentStatusRef = useRef<any>({
        isTodayChecked: false,
        isNeverChecked: true,
    })

    const onPlayHandle = () => {
        setPlayCount((preCount) => {
            return preCount + 1
        })
    }

    /**
     * 开始按钮
     */
    const onStartHandle = () => {
        trackStartBtn('click')
        setCurrentStep('loading')
        setCurrentVideoUrl([
            progressData?.nowNodeBeforeResource || '',
            progressData?.nowNodeAfterResource || '',
        ])
        onPlayHandle()

        setTimeout(() => {
            setCurrentStep('pre-check')
        }, 6000)
    }

    // 获取数据
    const getCheckInData = async (config?: {
        isInit?: boolean;       // 初始化操作
        isCheck?: boolean;      // 签到操作
        isReload?: boolean;     // 重新加载
    }) => {
        const { isInit, isCheck, isReload } = config || {}
        try {
            setLoading(true)
            let res: IBlackFridayCheckInData
            if (isInit && serverProgressData) {
                res = serverProgressData
            } else {
                res = await getBlackFriday2023CheckInProgress()
            }
            const {
                coverResource,
                coverResourceBak,
                nowProcessCheckInStatus,
                nowNodeAfterResource,
                nowNodeAfterResourceBak,
                nowNodeBeforeResource,
                nowNodeBeforeResourceBak,
                finalResource,
                finalResourceBak,
                isFinal,
                day1FirstResource,
                day1FirstResourceBak,
                checkedDayNum,
                totalPoints,
                activityStatus
            } = res

            // 今天已签到
            const isTodayChecked = !!nowProcessCheckInStatus
            // 从未签到
            const isNeverChecked = (checkedDayNum < 1 || !totalPoints) && !nowProcessCheckInStatus

            currentStatusRef.current = {
                isTodayChecked,
                isNeverChecked
            }

            setProgressData(res)

            if (!activityStatus) {
                setCurrentStep('not-started')
                setCurrentVideoUrl([coverResource, coverResourceBak])

            } else if (isNeverChecked) { // 从未签到
                if (!isReload) {
                    trackStartBtn('impr')
                    setCurrentStep('start')
                    setCurrentVideoUrl([day1FirstResource, day1FirstResourceBak])
                    onPlayHandle()
                }

            } else if (isCheck || (isTodayChecked && !isFinal)) { // 签到操作点击签到完成 播放下一个视频
                setCurrentStep('checked')
                setCurrentVideoUrl([nowNodeAfterResource, nowNodeAfterResourceBak])
                if (isCheck) {
                    onPlayHandle()
                }

            } else if (!isTodayChecked && !isNeverChecked) { // 未签到(非从未签到) 直接倒计时播放
                setCurrentStep('pre-check')
                setCurrentVideoUrl([nowNodeBeforeResource, nowNodeBeforeResourceBak ])

            } else if (isFinal) { // 所有签到完成
                setCurrentStep('checked')
                setCurrentVideoUrl([finalResource, finalResourceBak])
            }
        } catch (e) {
            console.log(e)
        } finally {
            setLoading(false)
        }
    }


    const onReloadData = async () => {
        await getCheckInData({
            isReload: true
        })
    }

    // 最后的资源
    const onSeeFinalReSource = () => {
        const {finalResource = '', finalResourceBak = ''} = progressData || {}
        setCurrentVideoUrl([finalResource, finalResourceBak])
        onPlayHandle()
    }

    // 签到
    const onCheckInHandle = async () => {
        try {
            setLoading(true)
            await blackFriday2023CheckIn()
            await getCheckInData({ isCheck: true })
        } catch (e) {
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        getCheckInData({ isInit: true }).then()
    }, [])

    return {
        currentVideoUrl,
        currentStep,
        progressData,
        loading,
        playCount,
        isLogin,
        onStartHandle,
        onCheckInHandle,
        onPlayHandle,
        onSeeFinalReSource,
        onReloadData,
    }
}